/**
 * CommentsPanel — Comments en modo edición. (Doc 21 Fase 5.d-e)
 *
 * Diferencias clave vs `CommentsViewerPanel` (modo viewer):
 *
 *   - CommentsViewerPanel permite crear NUEVOS root comments (rol del
 *     reviewer) + replies + transitions.
 *   - CommentsPanel es persistente lateral (dentro de
 *     `<RightSidePanel>`); SOLO muestra comments de v−1 (regla
 *     server-side via `listActiveComments`) y NO permite crear
 *     comments nuevos — el autor del artifact los responde y
 *     transiciona.
 *
 * Ambos comparten `<CommentThreadCard>` (Fase 5.e refactor) que
 * encapsula el render del thread + acciones por rol.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, Loader2 } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  createReply,
  listActiveComments,
  listAllComments,
  updateCommentStatus,
  type ArtifactComment,
  type CommentStatus,
} from '../../api/comments'
import { listProjectMembers } from '../../api/project_members'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { canCommentOnArtifact, canReopenComment, isExternal } from '../../lib/tiers'
import { pushToast } from '../../stores/toastStore'
import { CommentAiAssistModal } from './comments/CommentAiAssistModal'
import { parseInstanceKey } from './comments/commentAnchorText'
import { CommentsHistoryView } from './comments/CommentsHistoryView'
import {
  CommentsViewToggle,
  type CommentsViewKey,
} from './comments/CommentsViewToggle'
import {
  CommentThreadCard,
  groupCommentsByThread,
} from './comments/CommentThreadCard'
import {
  getValueAtPath,
  type TemplateI18nSection,
} from './official-viewer/section_types'

// ─── Types ──────────────────────────────────────────────────────────────────
export interface ApplyAiSuggestionPayload {
  /** Section_id de la sección anclada al comment. */
  sectionId: string
  /** Path-encoded instance_key del comment (para repetibles). */
  instanceKey: string
  /** Texto/markdown que el agente propuso. Disponible siempre — es el
   *  shape legacy y lo seguimos usando para el diff visual y para
   *  copy-paste. */
  suggestedText: string
  /** Doc 24 rework — JSON canónico tipado del nuevo contenido. Su
   *  forma depende del section_type (string para rich_text, array
   *  para listas/tablas, dict para code/mermaid, etc.). Cuando viene
   *  presente, el handler lo aplica DIRECTO al state del editor sin
   *  pasar por `convertAiPayloadForSection` (parser de markdown). Es
   *  `null` solo para rows legacy pre-rework. */
  suggestedValue: unknown | null
}

export interface CommentsPanelProps {
  artifactId: string
  /** Doc 07 — version target. Cuando el editor opera sobre una v(N+1)
   *  draft post-canon (artifact.status sigue APPROVED, current_version
   *  apunta a v(N) canon), pasamos esa draft explícita. Sin este id se
   *  resuelve a current_version_id en el backend. */
  versionId?: string | null
  /** ID del usuario actual — para badges, permission gates. */
  currentUserId: string | null
  /** ID del autor del artifact — para mostrar el botón "Subsanado"
   *  solo a esa persona. */
  artifactAuthorId: string | null
  /** OT-0009 — espejo de `_is_transition_allowed`: reponer un
   *  comentario resuelto exige ser el líder del proyecto, sin
   *  excepción de admin. El caller ya lo calcula (mismo `isLead` de
   *  `useProjectAccess` que usa para sus propios gates de edición). */
  isLead: boolean
  /** OT-0016 — generar, aplicar y descartar una sugerencia de IA exigen
   *  en el backend `load_artifact_for_edit` (dueño actual ∪ líder ∪
   *  admin). Es `canEditArtifact`, que el caller ya calcula para sus
   *  propios gates; se reusa aquí sin recalcularlo. Con `false` no se
   *  ofrece la asistencia de IA: ni el botón ni el modal. No depende de
   *  `artifactAuthorId` (el creador), que puede ya no ser el dueño. */
  canUseAiSuggestions: boolean
  projectId: string
  /** Mapa section_id → i18n para resolver títulos legibles del header
   *  de cada grupo de comments. */
  templateI18nMap: Map<string, TemplateI18nSection>
  /** Doc 21 Fase 10 — content vivo del artifact en pantalla. El panel
   *  resuelve por cada comment su valor anclado (via instance_key) y
   *  lo pasa a `CommentThreadCard` para que el `CommentDiffBlock`
   *  muestre el diff snapshot vs vivo. Solo se aplica a la vista
   *  Activos — el historial muestra evidencia frozen únicamente.
   *  Si no se pasa, el bloque cae al render original sin comparación. */
  versionContent?: Record<string, unknown> | null
  /** Callback opcional cuando el count de open comments cambia. */
  onOpenCountChange?: (count: number) => void
  /** Doc 21 Fase 7.g — el padre (route del editor) inyecta el handler
   *  que aplica la sugerencia IA al `content` del editor. Sin esto, el
   *  botón "Aplicar al borrador" del modal solo marca `applied_at` pero
   *  no toca el editor — usado en contextos read-only (viewer). */
  onApplyAiSuggestion?: (payload: ApplyAiSuggestionPayload) => void
}

// ─── Component ──────────────────────────────────────────────────────────────
export function CommentsPanel({
  artifactId,
  versionId,
  currentUserId,
  artifactAuthorId,
  isLead,
  canUseAiSuggestions,
  projectId,
  templateI18nMap,
  versionContent,
  onOpenCountChange,
  onApplyAiSuggestion,
}: CommentsPanelProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const { me, isLoading: meIsLoading } = useMe()
  const queryClient = useQueryClient()

  // Doc 21 Fase 6.b — toggle entre "Activos" (regla v−1, lo que está
  // por subsanar) e "Historial" (todos los comments de todas las
  // versions). Default Activos porque es la acción inmediata del autor.
  const [view, setView] = useState<CommentsViewKey>('active')

  // Doc 21 Fase 7.e — el modal IA co-reviewer se abre al clickear
  // "Subsanar con IA" sobre un comment del autor del artifact.
  // Estado local del CommentsPanel para evitar duplicar la modal por
  // cada thread; un solo render se reusa con el comment activo.
  const [aiModalComment, setAiModalComment] = useState<ArtifactComment | null>(
    null,
  )

  // Mismo cache key que EditView para que invalidate funcione cross
  // componentes (badge en toolbar + lista en el panel).
  const activeCommentsQueryKey = useMemo(
    () => [
      'artifacts',
      artifactId,
      'active-comments',
      versionId ?? null,
    ] as const,
    [artifactId, versionId],
  )

  const activeQuery = useQuery({
    queryKey: activeCommentsQueryKey,
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listActiveComments(accessToken, artifactId, {
        versionId: versionId ?? null,
      })
    },
    enabled: !!accessToken,
    staleTime: 15_000,
  })

  // Doc 21 Fase 6.b — query del historial completo. Solo carga cuando
  // el user activa la vista "Historial" para no traer todos los
  // comments por default.
  const historyQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'all-comments'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listAllComments(accessToken, artifactId)
    },
    enabled: !!accessToken && view === 'history',
    staleTime: 15_000,
  })

  // OT-0009 M4 — deshabilitada para `external`, mismo motivo que en
  // `CommentsViewerPanel.tsx`: `canCommentOnArtifact` no mira `isMember`
  // para ese tier, así que la query solo generaba un 404 de más.
  // `!meIsLoading` evita la carrera con `useMe()`.
  const membersQuery = useQuery({
    queryKey: ['projects', projectId, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, projectId)
    },
    enabled: !!accessToken && !meIsLoading && !isExternal(me?.role),
    staleTime: 60_000,
  })

  const displayNameByUserId = useMemo(() => {
    const m = new Map<string, string>()
    for (const member of membersQuery.data ?? []) {
      if (member.user_id) {
        m.set(
          member.user_id,
          member.user_display_name ??
            member.external_email ??
            t('comments_panel.unknown_user'),
        )
      }
    }
    return m
  }, [membersQuery.data, t])

  // OT-0009 M4 — espejo de `can_comment_on_artifact`. Sin esto, un
  // interno que solo ve el proyecto por `can_view_all_projects` (no es
  // `project_members`) veía los botones de responder/subsanar/
  // resolver/reabrir y el backend los rechazaba con 404 al enviarlos.
  const isMember = useMemo(
    () => (membersQuery.data ?? []).some((m) => m.user_id === currentUserId),
    [membersQuery.data, currentUserId],
  )
  const canComment = canCommentOnArtifact(me?.role, me?.tenant?.access, { isMember })
  // OT-0009 — hallazgo del sweep adversarial: "Reponer" tenía una
  // regla más estricta que las demás transiciones (solo el líder,
  // sin excepción de admin) y quedaba gateado con `canComment` a
  // secas, igual que las otras 5 acciones de comentario.
  const canReopen = canReopenComment(me?.tenant?.access, { isLead })

  const threads = useMemo(
    () => groupCommentsByThread(activeQuery.data ?? []),
    [activeQuery.data],
  )

  // Doc 21 Fase 10 — resuelve el valor vivo de la sección anclada a un
  // comment. parseInstanceKey decodifica el path-encoding del backend
  // (`name[idx].name`) y getValueAtPath navega el JSONB. Si no hay
  // versionContent (caller no lo pasó) o el path no resuelve, regresa
  // undefined y el CommentDiffBlock omite la comparación.
  const getLiveSectionValue = useMemo(() => {
    return (instanceKey: string): unknown => {
      if (!versionContent) return undefined
      const path = parseInstanceKey(instanceKey)
      if (path.length === 0) return undefined
      return getValueAtPath(versionContent, path)
    }
  }, [versionContent])

  const openCount = threads.length
  useEffect(() => {
    onOpenCountChange?.(openCount)
  }, [openCount, onOpenCountChange])

  // ─── Mutations ────────────────────────────────────────────────────────
  const replyMutation = useMutation({
    mutationFn: (input: { commentId: string; body: string }) => {
      if (!accessToken) throw new Error('no_access_token')
      return createReply(accessToken, input.commentId, { body: input.body })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: activeCommentsQueryKey,
      })
    },
    onError: (err) =>
      showApiToast(err, 'comments_panel.toast.reply_failed', t),
  })

  const statusMutation = useMutation({
    mutationFn: (input: {
      commentId: string
      status: CommentStatus
      note?: string | null
    }) => {
      if (!accessToken) throw new Error('no_access_token')
      const body: { status: CommentStatus; note?: string | null } = {
        status: input.status,
      }
      if (input.note !== undefined) body.note = input.note
      return updateCommentStatus(accessToken, input.commentId, body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: activeCommentsQueryKey,
      })
    },
    onError: (err) =>
      showApiToast(err, 'comments_panel.toast.status_failed', t),
  })

  // ─── Render gates ─────────────────────────────────────────────────────
  // El loading de members siempre aplica (lo necesitan ambas vistas).
  // El loading de la vista activa solo bloquea cuando estamos viéndola.
  const isLoadingPrimary =
    view === 'active' ? activeQuery.isLoading : historyQuery.isLoading
  if (meIsLoading || isLoadingPrimary || membersQuery.isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <CommentsViewToggle view={view} onChange={setView} />
        <div className="flex items-center justify-center py-12 text-ink-subtle">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          <span className="text-sm">{t('comments_panel.loading')}</span>
        </div>
      </div>
    )
  }

  if (
    (view === 'active' && activeQuery.error) ||
    (view === 'history' && historyQuery.error)
  ) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <CommentsViewToggle view={view} onChange={setView} />
        <div className="m-5 rounded-md border border-critical/40 bg-critical-soft p-3 text-xs text-critical flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{t('comments_panel.error_loading')}</span>
        </div>
      </div>
    )
  }

  return (
    // h-full + min-h-0 obligan al list de abajo a respetar la altura del
    // RightSidePanel y habilitan scroll interno. Sin esto el contenido
    // se desbordaba (parent es overflow-hidden) y no se podía scrollear.
    <div className="flex flex-col h-full min-h-0">
      <CommentsViewToggle view={view} onChange={setView} />
      {view === 'active' ? (
        <ActiveView
          threads={threads}
          openCount={openCount}
          currentUserId={currentUserId}
          artifactAuthorId={artifactAuthorId}
          displayNameByUserId={displayNameByUserId}
          templateI18nMap={templateI18nMap}
          getLiveSectionValue={getLiveSectionValue}
          replyMutation={replyMutation}
          statusMutation={statusMutation}
          canComment={canComment}
          canReopen={canReopen}
          onAiAssist={canUseAiSuggestions ? setAiModalComment : undefined}
          t={t}
        />
      ) : (
        <CommentsHistoryView
          comments={historyQuery.data ?? []}
          currentUserId={currentUserId}
          artifactAuthorId={artifactAuthorId}
          displayNameByUserId={displayNameByUserId}
          templateI18nMap={templateI18nMap}
        />
      )}

      {aiModalComment !== null ? (
        <CommentAiAssistModal
          open={true}
          onClose={() => setAiModalComment(null)}
          artifactId={artifactId}
          comment={aiModalComment}
          sectionTitle={sectionTitle(
            aiModalComment.section_id,
            aiModalComment.instance_key,
            templateI18nMap,
          )}
          onApply={(suggestion) => {
            // Doc 21 Fase 7.g — si el padre (route del editor) inyectó
            // el handler de apply, lo invocamos con el comment activo +
            // texto sugerido. El handler hace conversion type-aware
            // (markdown → TipTap doc, list → string[], etc.) y persiste.
            if (onApplyAiSuggestion) {
              onApplyAiSuggestion({
                sectionId: aiModalComment.section_id,
                instanceKey: aiModalComment.instance_key,
                suggestedText: suggestion.suggested_text,
                suggestedValue: suggestion.suggested_value,
              })
              pushToast({
                kind: 'success',
                message: t('comments_ai_modal.toast.applied_to_draft'),
                duration: 4000,
              })
            } else {
              // Contexto read-only (viewer) — el modal se cierra y
              // marcamos applied_at en BD, pero no hay editor que
              // tocar.
              pushToast({
                kind: 'info',
                message: t('comments_ai_modal.toast.applied'),
                duration: 4000,
              })
            }
          }}
        />
      ) : null}
    </div>
  )
}

// ─── ActiveView ─────────────────────────────────────────────────────────
function ActiveView({
  threads,
  openCount,
  currentUserId,
  artifactAuthorId,
  displayNameByUserId,
  templateI18nMap,
  getLiveSectionValue,
  replyMutation,
  statusMutation,
  canComment,
  canReopen,
  onAiAssist,
  t,
}: {
  threads: ReturnType<typeof groupCommentsByThread>
  openCount: number
  currentUserId: string | null
  artifactAuthorId: string | null
  displayNameByUserId: Map<string, string>
  templateI18nMap: Map<string, TemplateI18nSection>
  /** Doc 21 Fase 10 — resuelve el valor vivo de la sección anclada a
   *  un comment para el diff snapshot vs vivo en `CommentDiffBlock`. */
  getLiveSectionValue: (instanceKey: string) => unknown
  replyMutation: { isPending: boolean; mutate: (i: { commentId: string; body: string }) => void }
  statusMutation: { isPending: boolean; mutate: (i: { commentId: string; status: CommentStatus; note?: string | null }) => void }
  /** OT-0009 M4 — espejo de `can_comment_on_artifact`. `false` retira
   *  las callbacks de responder/transicionar de `CommentThreadCard`,
   *  que las usa para decidir si pinta el button (sin callback, no
   *  hay button). */
  canComment: boolean
  /** OT-0009 — espejo de `_is_transition_allowed`: solo el líder del
   *  proyecto puede reponer un comentario resuelto, sin excepción de
   *  admin. Independiente de `canComment`. */
  canReopen: boolean
  /** Callback Fase 7.e — abre el modal IA co-reviewer con el comment
   *  clickeado. El padre maneja el estado de qué comment está activo.
   *  `undefined` cuando el usuario no puede editar el artefacto
   *  (OT-0016): sin callback, `CommentThreadCard` no pinta el botón. */
  onAiAssist: ((comment: ArtifactComment) => void) | undefined
  t: TFunction
}) {
  if (threads.length === 0) {
    return (
      <div className="px-5 py-12 text-center">
        <Check className="w-6 h-6 text-official mx-auto mb-2" />
        <p className="font-display text-sm font-semibold text-ink">
          {t('comments_panel.empty.title')}
        </p>
        <p className="mt-1 text-xs text-ink-muted">
          {t('comments_panel.empty.body')}
        </p>
      </div>
    )
  }
  return (
    <>
      <div className="shrink-0 bg-review-soft/30 border-b border-review/20 px-4 py-2.5 text-[11px] text-ink-muted">
        {t('comments_panel.helper_banner', { count: openCount })}
      </div>
      <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-line">
        {threads.map((thread) => (
          <li key={thread.root.id}>
            <CommentThreadCard
              thread={thread}
              currentUserId={currentUserId}
              artifactAuthorId={artifactAuthorId}
              displayNameByUserId={displayNameByUserId}
              sectionTitle={sectionTitle(
                thread.root.section_id,
                thread.root.instance_key,
                templateI18nMap,
              )}
              liveSectionValue={getLiveSectionValue(
                thread.root.instance_key,
              )}
              isReplying={replyMutation.isPending}
              isStatusChanging={statusMutation.isPending}
              showStatusActions={true}
              showEditDeleteActions={false}
              onReply={
                canComment
                  ? (body) =>
                      replyMutation.mutate({
                        commentId: thread.root.id,
                        body,
                      })
                  : undefined
              }
              onMarkAddressed={
                canComment
                  ? (note) =>
                      statusMutation.mutate({
                        commentId: thread.root.id,
                        status: 'addressed',
                        note: note ?? null,
                      })
                  : undefined
              }
              onResolve={
                canComment
                  ? () =>
                      statusMutation.mutate({
                        commentId: thread.root.id,
                        status: 'resolved',
                      })
                  : undefined
              }
              onReopen={
                canReopen
                  ? () =>
                      statusMutation.mutate({
                        commentId: thread.root.id,
                        status: 'pending',
                      })
                  : undefined
              }
              onAiAssist={
                onAiAssist ? () => onAiAssist(thread.root) : undefined
              }
            />
          </li>
        ))}
      </ul>
    </>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function sectionTitle(
  sectionId: string,
  instanceKey: string,
  templateI18nMap: Map<string, TemplateI18nSection>,
): string {
  const i18n = templateI18nMap.get(sectionId)
  const base = i18n?.title ?? sectionId
  if (instanceKey && instanceKey !== sectionId) {
    return `${base} · ${instanceKey}`
  }
  return base
}

function showApiToast(
  err: unknown,
  fallbackKey: string,
  t: TFunction,
): void {
  const code = err instanceof ApiError ? err.code : null
  const fallback = t(fallbackKey)
  const message = code
    ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
    : err instanceof Error
      ? err.message
      : fallback
  pushToast({ kind: 'critical', message, duration: 6000 })
}
